import { useState, useEffect, useRef, useCallback } from 'react';
import type { Map } from 'mapbox-gl';
import { BeatAPIBannerRepository } from 'ts-core';
import type { Banner } from 'ts-core';
import { useApi, useTranslations } from 'react-ui';

const DEBOUNCE_MS = 800;

interface UseBannersV2Return {
  banners: Banner[];
  loading: boolean;
  error: Error | null;
  repository: BeatAPIBannerRepository;
}

/**
 * Fetches v2 banners from GET /api/v2/banners on each map move/zoom or
 * when the active tag filter changes.
 * Debounced at 800 ms to avoid excessive API traffic.
 *
 * @param map - The Mapbox GL map instance.
 * @param tags - Active tag slug filters from the query state. When non-empty,
 *               only banners tagged with any of those slugs are returned.
 */
export function useBannersV2(map: Map | null, tags: string[] = []): UseBannersV2Return {
  const { apiEndpoints, apiKey } = useApi();
  const { language } = useTranslations();

  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const repositoryRef = useRef<BeatAPIBannerRepository>(
    new BeatAPIBannerRepository(apiEndpoints.banners, apiKey ?? '')
  );
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const languageRef = useRef(language);
  const tagsRef = useRef(tags);

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  useEffect(() => {
    tagsRef.current = tags;
  }, [tags]);

  useEffect(() => {
    repositoryRef.current.url = apiEndpoints.banners;
  }, [apiEndpoints.banners]);

  useEffect(() => {
    if (apiKey != null) {
      repositoryRef.current.apiKey = apiKey;
    }
  }, [apiKey]);

  const fetchBanners = useCallback(async (mapInstance: Map) => {
    const bounds = mapInstance.getBounds();
    if (!bounds) return;

    const nw = bounds.getNorthWest();
    const se = bounds.getSouthEast();

    setLoading(true);
    setError(null);
    try {
      const data = await repositoryRef.current.getBanners({
        top_left: [nw.lat, nw.lng],
        bottom_right: [se.lat, se.lng],
        locale: languageRef.current,
        tags: tagsRef.current.length > 0 ? tagsRef.current : undefined,
      });
      // Sort by priority desc so the highest-priority banner is on top of the stack.
      setBanners([...data].sort((a, b) => b.priority - a.priority));
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch immediately when tags change (no debounce needed — user action)
  useEffect(() => {
    if (!map) return;
    fetchBanners(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, map, fetchBanners]);

  useEffect(() => {
    if (!map) return;

    const handler = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        fetchBanners(map);
      }, DEBOUNCE_MS);
    };

    map.on('moveend', handler);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      map.off('moveend', handler);
    };
  }, [map, fetchBanners]);

  return { banners, loading, error, repository: repositoryRef.current };
}
